import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface ViewDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onDelete?: () => void;
}

const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({ open, onClose, onDelete }) => {
  const { selectedDocument, loading } = useAppSelector((state) => state.documents);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle del Documento</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedDocument ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Nombre del Archivo
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDocument.fileName}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Nombre Original
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDocument.originalFileName}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tamaño del Archivo
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatFileSize(selectedDocument.fileSize)}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tipo MIME
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDocument.mimeType}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                URI
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, wordBreak: 'break-all' }}>
                {selectedDocument.uri}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Descripción
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDocument.description}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tags
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDocument.tags.join(', ')}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tipo de Documento
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDocument.documentType.name}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de Creación
              </Typography>
              <Typography variant="body1">
                {formatDate(selectedDocument.createdAt)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography>No hay documento seleccionado</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        {onDelete && selectedDocument && (
          <Button onClick={onDelete} variant="outlined" color="error">
            Eliminar
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDocumentModal;
